/**
 * The one banner above the frame (Holatlar 5.1b): the worst state shows and the rest fold behind
 * "+N". Being offline, the agency's credits, and for an owner a channel that went down. Nothing to
 * say draws nothing, so the page does not move. A failed check keeps the last known answer.
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { useState, useSyncExternalStore, type JSX } from "react";
import { Link } from "react-router";

import { agencyApi } from "@/api/agency";
import { messagingApi } from "@/api/messaging";
import { queryKeys } from "@/api/queryKeys";
import { useTranslation } from "@/i18n";
import { useAuthStore } from "@/store/auth";
import { Banner } from "@/ui/banner";
import { Button } from "@/ui/button";

import { channelAlerts, creditAlert, ordered, type ShellAlert } from "./alertsModel";

function subscribeOnline(onChange: () => void): () => void {
  window.addEventListener("online", onChange);
  window.addEventListener("offline", onChange);
  return (): void => {
    window.removeEventListener("online", onChange);
    window.removeEventListener("offline", onChange);
  };
}

function useOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, () => navigator.onLine);
}

function AlertBanner({ alert, more, onDismiss }: { alert: ShellAlert; more?: JSX.Element | undefined; onDismiss?: (() => void) | undefined }): JSX.Element {
  return (
    <Banner
      tone={alert.tone}
      onDismiss={onDismiss}
      action={
        <>
          {alert.action === undefined ? null : (
            <Button asChild size="sm" variant="secondary">
              <Link to={alert.action.to}>{alert.action.label}</Link>
            </Button>
          )}
          {more}
        </>
      }
    >
      {alert.text}
    </Banner>
  );
}

export function ShellAlerts(): JSX.Element | null {
  const { t } = useTranslation();
  const role = useAuthStore((state) => state.user?.role ?? undefined);
  const status = useAuthStore((state) => state.user?.tenant?.status);
  const online = useOnline();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());

  const standing = useQuery({
    queryKey: queryKeys.agency.standing(),
    queryFn: () => agencyApi.billingStanding(),
    staleTime: 5 * 60_000,
    enabled: role !== undefined,
    retry: false,
  });
  const accounts = useQuery({
    queryKey: queryKeys.messagingAccounts(),
    queryFn: () => messagingApi.accounts(),
    staleTime: 60_000,
    enabled: role === "owner",
    retry: false,
  });

  const credit = creditAlert({ status, daysLeft: standing.data?.days_left, credits: standing.data?.credits, canBuy: role === "owner" });
  const alerts = ordered([...(credit === null ? [] : [credit]), ...channelAlerts(accounts.data ?? [])]).filter((alert) => !dismissed.has(alert.key));
  const [first, ...rest] = alerts;

  return (
    <>
      {online ? null : (
        <Banner tone="warning">
          <b className="font-medium">{t("hol.offline.title")}.</b> {t("hol.offline.text")}
        </Banner>
      )}
      {first === undefined ? null : (
        <AlertBanner
          alert={first}
          onDismiss={
            first.tone === "danger"
              ? undefined
              : (): void => {
                  setDismissed((current) => new Set(current).add(first.key));
                }
          }
          more={
            rest.length === 0 ? undefined : (
              <button
                type="button"
                aria-expanded={open}
                onClick={() => {
                  setOpen((value) => !value);
                }}
                className="inline-flex items-center gap-1 text-sm font-medium"
              >
                +{rest.length}
                <ChevronDown className="size-4" strokeWidth={1.5} aria-hidden="true" />
              </button>
            )
          }
        />
      )}
      {open ? rest.map((alert) => <AlertBanner key={alert.key} alert={alert} />) : null}
    </>
  );
}
